import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
} from '@mui/material';
import { useField } from 'formik';

import { FormikSelectOption } from './formik-select-field.component';
import { formatMultipleSelectRenderValue } from './formik-select-multiple-field.utils';

export interface FormikSelectMultipleFieldProps {
  name: string;
  label: string;
  required?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  autoComplete?: string;
  onActive?: (active: boolean) => void;
  onChange?: (value: string[]) => void;
  sx?: SxProps;
  options: FormikSelectOption[];
  emptyValue?: string;
  helperText?: string;
}

export const FormikSelectMultipleField = (
  props: FormikSelectMultipleFieldProps,
) => {
  const [field, meta] = useField(props.name);
  const hasError = meta.touched && Boolean(meta.error);
  const selectedValues: string[] = Array.isArray(field.value)
    ? field.value
    : [];
  const isEmpty = selectedValues.length === 0;

  const handleChange = (e: SelectChangeEvent<string[]>) => {
    field.onChange(e);
    props.onChange?.(e.target.value as string[]);
  };

  return (
    <FormControl>
      <InputLabel
        id={`select-${props.label}`}
        required={props.required}
        shrink={isEmpty && props.emptyValue !== undefined ? true : undefined}
      >
        {props.label}
      </InputLabel>
      <Select
        multiple
        displayEmpty
        id={props.name}
        labelId={`select-${props.label}`}
        name={props.name}
        label={props.label}
        required={props.required}
        fullWidth={props.fullWidth ?? true}
        value={selectedValues}
        renderValue={(value) =>
          formatMultipleSelectRenderValue({
            value,
            options: props.options,
            emptyValue: props.emptyValue,
          })
        }
        onChange={handleChange}
        onBlur={(e) => {
          field.onBlur(e);
          if (props.onActive) props?.onActive(false);
        }}
        onFocus={() => {
          if (props.onActive) props.onActive(true);
        }}
        error={hasError}
        disabled={props.disabled}
        autoComplete={props.autoComplete ?? 'off'}
        sx={props.sx}
      >
        {props.options
          ?.filter((option) => !option.hidden)
          .map((option) => (
            <MenuItem
              id={`option-${option.id}`}
              key={option.id}
              value={option.id}
              disabled={option.disabled}
            >
              {option.displayName}
            </MenuItem>
          ))}
      </Select>

      {(props.helperText || hasError) && (
        <FormHelperText error={hasError}>
          {hasError ? meta.error : props.helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};
